import { useRef, useState, useCallback } from "react";

const SR =
    typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

export default function useSpeechRecognition({ onInterim, onFinal }) {
    const [isListening, setIsListening] = useState(false);
    const recRef = useRef(null);
    const callbacksRef = useRef({ onInterim, onFinal });
    callbacksRef.current = { onInterim, onFinal };

    const start = useCallback(() => {
        if (!SR) { alert("Speech recognition not supported."); return; }

        if (recRef.current) {
            recRef.current.onend = null;
            try { recRef.current.stop(); } catch (_) { }
        }

        const rec = new SR();
        
        // THE MAGIC FIX: Android completely breaks with continuous=true.
        // By making it false on mobile, the mic processes one phrase at a time,
        // cleanly finalizing it before restarting, eliminating all stacking bugs.
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        rec.continuous = !isMobile; 
        
        rec.interimResults = true;
        rec.lang = "en-US";

        rec.onstart = () => {
            setIsListening(true);
        };

        rec.onresult = (e) => {
            let finalChunk = "";
            let interimChunk = "";

            // Revert to the standard W3C spec loop
            for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) {
                    finalChunk += e.results[i][0].transcript;
                } else {
                    interimChunk += e.results[i][0].transcript;
                }
            }

            if (finalChunk) {
                callbacksRef.current.onFinal?.(finalChunk);
                callbacksRef.current.onInterim?.("");
            } else {
                callbacksRef.current.onInterim?.(interimChunk);
            }
        };

        rec.onend = () => {
            if (recRef.current === rec) {
                // Auto-restart to simulate continuous listening seamlessly
                setTimeout(() => {
                    try { rec.start(); } catch (_) { setIsListening(false); }
                }, 50); 
            } else {
                setIsListening(false);
            }
        };

        rec.onerror = (e) => {
            if (e.error === "no-speech") return;
            console.error("Speech error:", e.error);
        };

        recRef.current = rec;
        try { rec.start(); } catch (_) { }
    }, []);

    const stop = useCallback(() => {
        if (recRef.current) {
            recRef.current.onend = null;
            try { recRef.current.stop(); } catch (_) { }
            recRef.current = null;
        }
        setIsListening(false);
    }, []);

    return { isListening, start, stop };
}